import { freeze } from "immer";
import { Updater } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme";
import { ExpressionPayloadType, UrlMatch } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { TRAFFIC_CONFIG_QUERY_PARAM_OPTIONS } from "../../utils/constants";

import { AddConditionButton } from "./AddConditionButton";
import { Condition } from "./Condition";
import { useExpressionBuilderContext } from "./context";
import { pageTargetingUrlPathTypeDisabled } from "./utils";

interface ConditionGroupProps {
  expressionsList: ExpressionPayloadType[];
  setExpressionsList: Updater<ExpressionPayloadType[]>;
  expressionsListIndex: number;
  configuredQueryParams?: Set<string>;
}

export const ConditionGroup = ({
  expressionsList,
  setExpressionsList,
  expressionsListIndex,
  configuredQueryParams,
}: ConditionGroupProps) => {
  const ctx = useExpressionBuilderContext();
  const subExpressions = expressionsList[expressionsListIndex];

  const configuredUrlPathFilters =
    ctx?.renderContext === "pageTargeting"
      ? subExpressions.queries.reduce((acc, subexp) => {
          if (subexp.type === "urlPath" && subexp.filter) {
            acc.set(subexp.filter, (acc.get(subexp.filter) || 0) + 1);
          }
          return acc;
        }, new Map<UrlMatch, number>())
      : undefined;

  const setSubExpressions: Updater<ExpressionPayloadType> = (updater) => {
    setExpressionsList((draft) => {
      if (typeof updater === "function") updater(draft[expressionsListIndex]);
      else draft[expressionsListIndex] = freeze(updater);
    });
  };

  const onAddSubExp = () => {
    const nextType =
      ctx?.renderContext === "pageTargeting" &&
      pageTargetingUrlPathTypeDisabled(configuredUrlPathFilters)
        ? "jsExpression"
        : subExpressions.type;

    setExpressionsList((draft) => {
      draft[expressionsListIndex].queries.push({
        type: nextType,
        filter:
          nextType === "urlPath" && configuredUrlPathFilters?.size
            ? "contains"
            : "equals",
        key:
          nextType === "utm"
            ? Object.values(TRAFFIC_CONFIG_QUERY_PARAM_OPTIONS)[0]
            : "",
        value: "",
      });
    });
  };

  const removeExpression = (queryIndex: number) => {
    setExpressionsList((draft) => {
      if (draft[expressionsListIndex].queries.length === 1) {
        draft.splice(expressionsListIndex, 1);
      } else {
        draft[expressionsListIndex].queries.splice(queryIndex, 1);
      }
    });
  };

  if (!subExpressions.queries.length) return null;
  return (
    <Grid item container spacing={0}>
      {subExpressions.queries.map((_, i) => {
        return (
          <Condition
            key={i}
            expressions={subExpressions}
            setExpressions={setSubExpressions}
            expressionsIndex={i}
            removeExpression={removeExpression}
            configuredUrlPathFilters={configuredUrlPathFilters}
            configuredQueryParams={configuredQueryParams}
          />
        );
      })}
      <AddConditionButton
        nested={true}
        type={ctx?.renderContext === "audience" ? "or" : "and"}
        onClick={onAddSubExp}
        color="link"
      />
      {expressionsListIndex < expressionsList.length - 1 && (
        <Grid xs={12} item>
          <IgTypography color={"placeholder"} sx={{ fontWeight: 500 }}>
            {ctx?.renderContext === "audience" ? "AND" : "OR"}
          </IgTypography>
        </Grid>
      )}
    </Grid>
  );
};
